import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
	GridCellModes,
	GridCellModesModel,
	GridCellParams,
	GridRowId,
	GridRowModel,
	useGridApiRef,
} from '@innovyze/stylovyze';
import {
	selectRiskMultiScoreDataGrid,
	useSelectIsCountLoading,
	selectRiskCount,
	selectDataGridState,
} from '@Selectors';
import { Component, MultiScoreItem } from '@Types';
import { tableColumns, tableColumnsGrouping } from './TabelColumns';
import { StyledMultiScoreDataGrid } from './MultiScoreDataGrid.styles';
import { TableToolbar } from './TableToolbar';
import {
	setDataGridState,
	setRiskMultiScoreDataGrid,
	updateRiskMultiScoreDataGridRow,
} from '@Actions/util.actions';
import { NoParameters } from './NoParameters.component';

interface MultiScoreDataGridProps {
	dataGridKey: string;
	componentParameter1?: Component;
	componentParameter2?: Component;
	componentMatrix?: MultiScoreItem[];
	onRecalculateCount: () => void;
}

const MultiScoreDataGrid = ({
	dataGridKey,
	componentParameter1,
	componentParameter2,
	componentMatrix,
	onRecalculateCount,
}: MultiScoreDataGridProps) => {
	const dispatch = useDispatch();

	const apiRef = useGridApiRef();
	const dataGridState = selectDataGridState(dataGridKey);

	const scoreRows = selectRiskMultiScoreDataGrid();
	const riskCount = selectRiskCount('ScoreMatrix');
	const isRiskCountLoading = useSelectIsCountLoading('ScoreMatrix');

	const scoreMax = 1000;

	const [cellModesModel, setCellModesModel] =
		React.useState<GridCellModesModel>({});
	const [currentCellId, setCurrenCellId] = React.useState<
		GridRowId | undefined
	>(undefined);
	const [currentCellField, setCurrenCellField] = React.useState<
		string | undefined
	>(undefined);

	const handleCellClick = React.useCallback(
		(params: GridCellParams, event: React.MouseEvent) => {
			if (!params.isEditable) {
				return;
			}

			// Ignore portal
			if (!event.currentTarget.contains(event.target as Element)) {
				return;
			}

			setCurrenCellId(params.id);
			setCurrenCellField(params.field);

			setCellModesModel(prevModel => {
				return {
					// Revert the mode of the other cells from other rows
					...Object.keys(prevModel).reduce(
						(acc, id) => ({
							...acc,
							[id]: Object.keys(prevModel[id]).reduce(
								(acc2, field) => ({
									...acc2,
									[field]: { mode: GridCellModes.View },
								}),
								{},
							),
						}),
						{},
					),
					[params.id]: {
						// Revert the mode of other cells in the same row
						...Object.keys(prevModel[params.id] || {}).reduce(
							(acc, field) => ({
								...acc,
								[field]: { mode: GridCellModes.View },
							}),
							{},
						),
						[params.field]: { mode: GridCellModes.Edit },
					},
				};
			});
		},
		[],
	);

	const handleCellModesModelChange = React.useCallback(
		(newModel: GridCellModesModel) => {
			setCellModesModel(newModel);
		},
		[],
	);

	const onCellEditStop = () => {
		setCurrenCellId(undefined);
		setCurrenCellField(undefined);
	};

	useEffect(() => {
		if (
			componentMatrix &&
			componentParameter1 &&
			componentParameter1.metadata &&
			componentParameter2 &&
			componentParameter2.metadata
		) {
			const dataGridRows = Array<MultiScoreItem>();
			let index = 0;

			if (riskCount && riskCount.length !== 0) {
				for (let i = 0; i < componentParameter2.metadata.length; i++) {
					const row = { ...componentMatrix[i] };

					for (
						let i2 = 0;
						i2 < componentParameter1.metadata.length;
						i2++
					) {
						const cell = riskCount.find(value => value.i === index);

						if (cell) {
							row[`${i2}-count`] = cell.count;
						}
						index++;
					}

					dataGridRows.push(row);
				}
			} else {
				// For an empty count put a blank count so they can still score but it's more obvious that the count has now worked
				for (let i = 0; i < componentParameter2.metadata.length; i++) {
					const row = { ...componentMatrix[i] };

					for (
						let i2 = 0;
						i2 < componentParameter1.metadata.length;
						i2++
					) {
						row[`${i2}-count`] = '';
						index++;
					}

					dataGridRows.push(row);
				}
			}

			dispatch(setRiskMultiScoreDataGrid(dataGridRows));
		}
	}, [
		componentParameter1,
		componentParameter2,
		componentMatrix,
		riskCount,
		isRiskCountLoading,
	]);

	const processRowUpdate = React.useCallback(async (newRow: GridRowModel) => {
		const validRow = { ...newRow };
		const keys = Object.keys(validRow);

		// Validate the score range
		keys.forEach(key => {
			if (key.endsWith('-score')) {
				const score = validRow[key];
				if (!score || score < 0) validRow[key] = 0;
				else if (score > scoreMax) validRow[key] = scoreMax;
			}
		});

		// Update the redux
		dispatch(updateRiskMultiScoreDataGridRow(validRow));

		return validRow;
	}, []);

	const onStateChange = React.useCallback(() => {
		// We dont want to use the individual states that could be passed but just export the whole state to keep it all in sync
		const state = apiRef.current.exportState();
		dispatch(
			setDataGridState({
				key: dataGridKey,
				initialState: state,
			}),
		);
	}, [dataGridKey]);

	const onRecalculateCountMatrix = () => {
		if (currentCellId && currentCellField)
			apiRef.current.stopCellEditMode({
				id: currentCellId,
				field: currentCellField,
			});

		onRecalculateCount();
	};

	if (
		componentParameter1 &&
		componentParameter1.metadata &&
		componentParameter2 &&
		componentParameter2.metadata
	) {
		const rows =
			componentParameter2.metadata.length +
			(componentParameter2.metadataType === 'range' ? 1 : 0);

		return (
			<StyledMultiScoreDataGrid
				apiRef={apiRef}
				experimentalFeatures={{ columnGrouping: true }}
				disableColumnMenu
				disableColumnReorder
				loading={scoreRows.length === 0}
				skeletonRowCount={rows}
				columns={tableColumns(
					componentParameter1,
					componentParameter2,
					'ScoreMatrix',
					dataGridState,
				)}
				columnGroupingModel={tableColumnsGrouping(componentParameter1)}
				rows={scoreRows}
				processRowUpdate={processRowUpdate}
				slots={{
					toolbar: TableToolbar,
				}}
				slotProps={{
					toolbar: {
						isLoading: scoreRows.length === 0 || isRiskCountLoading,
						onRecalculateCount: onRecalculateCountMatrix,
					},
				}}
				cellModesModel={cellModesModel}
				onCellModesModelChange={handleCellModesModelChange}
				onCellClick={handleCellClick}
				onCellEditStop={onCellEditStop}
				// DONT USE onStateChange={onStateChange} AS THIS GETS CALLED FOR EVERY USER ACTION... SO PROBABLY BEST TO AVOID !!
				onColumnWidthChange={onStateChange}
				dataCy="multiscore-table"
			/>
		);
	} else {
		return <NoParameters />;
	}
};

export default MultiScoreDataGrid;
